import _ from 'lodash';
import { BaseItemStore } from '@/trendData/baseItem.store';
import { createItem, pruneDehydratedItemProps, shouldDehydrateItem } from '@/trendData/itemStore.utilities';
import { DASH_STYLES, ITEM_TYPES, Y_AXIS_TYPES } from '@/trendData/trendData.constants';
import { updateComputedYAxisExtremes } from '@/trendData/yAxis.actions';
import type { Item } from '@/trendData/baseItem.store';
import type { DehydratedScalarItem, ScalarItem } from '@/trendData/trendData.types';

interface ScalarAddPayload {
  id: string;
  name: string;
  lane: number;
  alignment: string;
  color?: string;
  laneReservation?: string;
}

export type ScalarDehydratedState = Pick<BaseItemStore<Item>, 'items'> & {
  items: DehydratedScalarItem[];
};

type ScalarItemUpdate = Partial<Omit<ScalarItem, 'warningLogs'>> & {
  warningLogs?: unknown[];
};

/**
 * A store for containing scalars which can be displayed on the chart. A scalar is composed of a single y-axis value.
 *
 * This store is augmented with additional functionality from sqBaseSignalStore.
 */

export class TrendScalarStore extends BaseItemStore<ScalarItem> {
  static readonly storeName = 'sqTrendScalarStore';

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @returns {Object} The dehydrated items.
   */
  dehydrate(): ScalarDehydratedState {
    return {
      items: _.chain(this.state.get('items'))
        .filter(shouldDehydrateItem)
        .map((item) => pruneDehydratedItemProps(item))
        .value() as DehydratedScalarItem[],
    };
  }

  /**
   * Re-creates the scalars. All necessary data needed to rehydrate is persisted so no actions have to be called.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   * @return {Object} A promise that is fulfilled when items are completely rehydrated.
   */
  rehydrate(dehydratedState: ScalarDehydratedState): void {
    this.state.set(
      'items',
      _.map(dehydratedState.items, (item) =>
        this.createScalar(
          item.id,
          item.name,
          item.lane,
          item.axisAlign,
          _.omit(item, ['id', 'name', 'lane', 'axisAlign']) as ScalarItemUpdate,
        ),
      ),
    );
  }

  /**
   * Adds a scalar item.
   *
   * @param {Object} payload - Object container for arguments
   * @param {String} payload.id - ID of the new item
   * @param {String} payload.name - Name of the new item
   * @param {String} payload.lane - Lane of the new item
   * @param {String} payload.alignment - Alignment of the new item
   * @param {String} [payload.color] - Color hex code (e.g. #CCCCCC)
   */
  addScalar(payload: ScalarAddPayload): void {
    this.state.push(
      'items',
      this.createScalar(
        payload.id,
        payload.name,
        payload.lane,
        payload.alignment,
        _.pick(payload, ['color', 'laneReservation']),
      ),
    );
  }

  /**
   * Merges the item's computed y-axis config when its results arrive so a new item is displayed
   * within its lane before the debounced lane recompute; the fetched values themselves live in
   * the item runtime store.
   */
  applyComputedAxisConfig(payload: { id: string }): void {
    const cursor = this.getItemCursor(payload.id);
    if (cursor.exists()) {
      cursor.merge(updateComputedYAxisExtremes(payload.id) as ScalarItemUpdate);
    }
  }

  /**
   * Private helper function to add a scalar to the store using the specified properties.
   *
   * @param {String} id - ID to use for the new scalar
   * @param {String} name - Name to use for the new scalar
   * @param {String} lane - Lane to use for the new series
   * @param {String} alignment - Alignment to use for the new series
   * @param {Object} props - Object containing properties to apply to the new scalar
   * @returns {Object} Newly created scalar object.
   */
  createScalar(id: string, name: string, lane: number, alignment: string, props: ScalarItemUpdate): ScalarItem {
    return createItem(
      id,
      name,
      ITEM_TYPES.SCALAR,
      _.assign(
        {
          data: [],
          dashStyle: DASH_STYLES.DASH,
          isStringSeries: false,
          yAxisConfig: {},
          lane,
          axisAlign: alignment,
          axisVisibility: true,
          axisAutoScale: true,
          visible: true,
          lineWidth: 1,
          yAxisType: Y_AXIS_TYPES.LINEAR,
        },
        props,
      ),
    ) as ScalarItem;
  }

  private readonly localHandlers = {
    TREND_ADD_SCALAR: (payload: ScalarAddPayload) => this.addScalar(payload),
    TREND_SCALAR_RESULTS_SUCCESS: (payload: { id: string }) => this.applyComputedAxisConfig(payload),
  };

  protected readonly handlers = { ...this.localHandlers, ...this.baseHandlers };
}
